import React from "react";
import { useSelector } from "react-redux";
import { object, string } from "prop-types";
import get from "lodash/get";
import "./breaking-news-prominent.m.css";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { HeroImage } from "../../atoms/HeroImage";
import { RowSeparator } from "../../atoms/RowSeparator";
import { useDeviceType } from "../../custom-hooks/useDeviceType";
import { Link } from "@quintype/components";
import { DfpComponent } from "../../ads/dfp-component";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import { Label } from "../../atoms/Label";
import Headline from "../../atoms/Headline";
import SubHeadline from "../../atoms/SubHeadline";

export default function BreakingNewsProminent({ collection, extraProp = "" }) {
  const {
    show_all_stories,
    show_first_story_hero_image,
    show_timestamp,
    show_subheadline,
    show_read_time,
    show_row_separator,
    show_column_separator,
    show_row_ad_unit_bottom,
  } = get(collection, ["associated-metadata"], {});

  const allStories = get(collection, ["items"], []).filter((item) => item.type === "story");
  const [firstStory, ...otherStories] = allStories;
  const bottomAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_bottom_ad"], {})
  );
  const rowAdBottom = show_row_ad_unit_bottom?.split("/").slice(-1)[0];
  const rowAdBottomDivId = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "ad_div_mapping", rowAdBottom], "")
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));

  const sectionSlug = extraProp;

  if (!(firstStory?.story && Object.keys(firstStory?.story).length > 0)) {
    return null;
  }
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  const checkMin6Images = allStories.length >= 6;

  const customStyleForFirstStoryReadTime = {
    color: "white",
  };
  const externalStoryUrl = isExternalStory(firstStory?.story);
  const href = externalStoryUrl || `/${firstStory?.story?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";

  const SingleStoryCard = ({ story }) => {
    const { subheadline } = story;
    const externalStoryUrl = isExternalStory(story);
    const href = externalStoryUrl || `/${story?.slug}`;
    const target = externalStoryUrl ? "_blank" : "_self";
    return (
      <Link href={href} target={target} styleName="story-link">
        <div styleName="story-col-single">
          {getStoryLabel(story) && <Label story={story} />}
          {getStoryHeadline(story) && (
            <Headline headline={getStoryHeadline(story)} styleName="story-single-title" sectionSlug={sectionSlug} />
          )}
          {subheadline && show_subheadline && (
            <SubHeadline styleName="story-single-description" subHeadline={subheadline} sectionSlug={sectionSlug} />
          )}
          <PublishAndReadTime showReadTime={show_read_time} showTimestamp={show_timestamp} story={story} />
        </div>
      </Link>
    );
  };

  const DoubleStoryCard = ({ story }) => {
    const externalStoryUrl = isExternalStory(story);
    const href = externalStoryUrl || `/${story?.slug}`;
    const target = externalStoryUrl ? "_blank" : "_self";
    return (
      <Link href={href} target={target} styleName="story-link">
        <div styleName="story-col-double-nest">
          {getStoryLabel(story) && <Label story={story} />}
          {getStoryHeadline(story) && (
            <Headline
              headline={getStoryHeadline(story)}
              styleName="story-col-double-nest-title"
              sectionSlug={sectionSlug}
            />
          )}
          <PublishAndReadTime showReadTime={show_read_time} showTimestamp={show_timestamp} story={story} />
        </div>
      </Link>
    );
  };

  return (
    <>
      <div className="row-component" styleName="container">
        <Link href={href} target={target} styleName="story-link">
          <div styleName="top">
            {getStoryLabel(firstStory.story) && <Label story={firstStory.story} />}
            {getStoryHeadline(firstStory?.story) && (
              <Headline headline={getStoryHeadline(firstStory?.story)} styleName="second" sectionSlug={sectionSlug} />
            )}
            {firstStory?.story?.subheadline && show_subheadline && (
              <SubHeadline subHeadline={firstStory?.story?.subheadline} styleName="third" sectionSlug={sectionSlug} />
            )}
            <PublishAndReadTime
              showReadTime={show_read_time}
              showTimestamp={show_timestamp}
              story={firstStory.story}
              customStyle={customStyleForFirstStoryReadTime}
              customDotColor="white"
            />
          </div>
        </Link>
        {(show_first_story_hero_image || show_all_stories) && (
          <div styleName="middle">
            <HeroImage
              story={firstStory.story}
              widths={[1110]}
              aspectRatio={[[11.1, 4]]}
              headline={firstStory.story?.headline}
              iconSizes={{ width: 48, height: 48 }}
            />
          </div>
        )}
        {checkMin6Images && show_all_stories && (
          <div styleName="bottom">
            <SingleStoryCard story={otherStories[0].story} />
            <div styleName={`story-col-double ${!show_column_separator ? "column-separator-hide" : ""}`}>
              <DoubleStoryCard story={otherStories[1].story} />
              <div styleName="story-col-double-horizontal-separator"></div>
              <DoubleStoryCard story={otherStories[2].story} />
            </div>
            <div styleName="story-col-double-horizontal-separator only-mobile"></div>
            <div styleName={`story-col-double ${!show_column_separator ? "column-separator-hide" : ""}`}>
              <DoubleStoryCard story={otherStories[3].story} />
              <div styleName="story-col-double-horizontal-separator"></div>
              <DoubleStoryCard story={otherStories[4].story} />
            </div>
          </div>
        )}
        <div styleName="row-separator-container">{show_row_separator && <RowSeparator />}</div>
      </div>
      {show_row_ad_unit_bottom && <DfpComponent adStyleName="ad-slot-size-300x250" id={rowAdBottomDivId} />}
    </>
  );
}

BreakingNewsProminent.propTypes = {
  collection: object.isRequired,
  extraProp: string,
};

BreakingNewsProminent.storyLimit = 6;
